import React, { useEffect } from 'react';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { UserRegistrationShell } from '@src/common/UserRegistrationShell';
import { UHCProviderLink } from '@src/common/UHCProviderLink';
import { UHCLink } from '@src/common/UHCLink';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useTIN } from '@src/state/taxInformation';
import { useOnloading } from '@src/state/landing';
import { getOrgType } from '@src/common/helpers';
import { useUser } from '@src/state/user';
import { config } from '@uhg-abyss/web/tools/config';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginTop: '50px',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const SaveDescription = styled('p', {
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: '$primary',
});

const ButtonView = styled('div', {
  display: 'flex',
  margin: 'auto',
  marginTop: '15px',
  flexDirection: 'row',
  justifyContent: 'space-between',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  fontSize: '16px',
});

const ContactLabel = styled('span', {
  color: '$info1',
});

export const PAARequiredMessage = () => {
  const router = useRouter();
  const { token } = useParams();
  const { navigate } = useRouter();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { currentFlow } = useOnloading.state();
  const { TINNumber, TINInfo } = useTIN.state();

  const lagoonContext = React.useContext(LagoonContext);
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const featureToggles = lagoonContext.getTable('feature-toggles');

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'paa required';
      const pageName = `digitalsecurity:${section1}:${section2}`;

      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
      });
    }
  }, []);

  return (
    <UserRegistrationShell activeStep="paa required message">
      <MainContainer>
        <IconBrand
          icon="alert"
          style={{
            height: '70px',
            width: '70px',
            marginTop: '10px',
            marginBottom: '10px',
          }}
          variant="twotone"
        />
        <BoxHeader>
          <BoxHeaderText>
            We're sorry!{' '}
            {isAPIMarketPlaceUser
              ? 'Your organization needs to designate a Secret Keeper before we can provide access to the UnitedHealthcare API Marketplace.'
              : 'Your organization needs to designate a Primary Access Administrator before we can provide access to the UnitedHealthcareProvider Portal.'}
          </BoxHeaderText>
        </BoxHeader>
        <SaveDescription>
          {isAPIMarketPlaceUser ? (
            <React.Fragment>
              Please email us at{' '}
              <ContactLabel>apiconsultant@uhc.com</ContactLabel> for additional
              assistance.
            </React.Fragment>
          ) : (
            <React.Fragment>
              For additional assistance, please contact our UnitedHealthcare Web
              Support team by clicking the chat icon on the bottom right of this
              screen. Support is also available by calling 866-842-3278, option
              1.
            </React.Fragment>
          )}
        </SaveDescription>
      </MainContainer>
      <Flex.Content>
        <ButtonView>
          <BackButton
            onClick={() => {
              return navigate(-1);
            }}
          >
            <IconSymbol icon="arrow_back" size={20} color="$primary1" />
            <BackButtonText>Back</BackButtonText>
          </BackButton>
          <NextButton
            onClick={() => {
              return router.navigate(`/${token}`);
            }}
          >
            <NextButtonText>Start Over</NextButtonText>
          </NextButton>
        </ButtonView>
      </Flex.Content>
      {isAPIMarketPlaceUser ? (
        <UHCLink
          label="Return to API Marketplace"
          url={
            uiMessages.getRow('API_MARKETPLACE_WELCOME_URL')?.message ||
            config('API_MARKETPLACE_WELCOME_URL')
          }
          sameTab
        />
      ) : (
        <React.Fragment>
          <UHCProviderLink
            style={{
              margin: 'auto',

              display: 'inline-flex',
              marginTop: '20px',
              flexDirection: 'row',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          />
          <br />
          <UHCLink
            label="Return to Provider Portal"
            url={uiMessages.getRow('SDE_URL')?.message || config('SDE_URL')}
          />
        </React.Fragment>
      )}
    </UserRegistrationShell>
  );
};
